import {AutogenLayoutReadyToSubmitSlideout} from "./autogen/AutogenLayoutReadyToSubmitSlideout";
import {Button, DataSourceMode, Label, Panel, SelectionMode, Snackbar, TableRow, Toast} from "@mcleod/components";
import {Api, Currency, HorizontalAlignment, ModelRow} from "@mcleod/core";
import {SubmittalSettlementsTable} from "./SubmittalSettlementsTable";
import {TriumphNotifications} from "./TriumphNotifications";

export class ReadyToSubmitSlideout extends AutogenLayoutReadyToSubmitSlideout {
    readyToSubmitButton = new Button({
        id: "submitToTriumpPaySaveButton",
        caption: "Submit",
        backgroundColor: "primary",
        borderWidth: 0,
        rowBreak: false,
        enabled: true,
        onClick: async () => {
            const settlementsTable = this.layoutSubmittalSettlements;
            const selectedRows = settlementsTable.tableReadyToSubmitOrderPayables.selectedRows;
            if (selectedRows.length === 0) {
                Snackbar.showWarningSnackbar("Please select at least one settlement to submit.");
                return;
            }
            try {
                this.readyToSubmitButton.busy = true;

                const settlementIds = selectedRows.map(row => row.data.get("record_id"));
                const response = await Api.post("lme/settlement/submit-settlement-to-triumphpay", {
                    settlement_id_list: settlementIds
                });

                const {
                    error,
                    successful_settlement_count,
                    failure_count,
                    successful_settlement_list,
                    is_update
                } = response?.data[0];

                if (successful_settlement_count > 0) {
                    // Get currency from first selected settlement. For now we are only able to submit same currency settlements
                    const baseCurrency = selectedRows[0].data.getCurrency("settlement_total");
                    const submittedAmount = new Currency({ currency_code: baseCurrency.currency_code, amount: 0 });

                    for (const row of selectedRows) {
                        if (successful_settlement_list.includes(row.data.get("record_id"))) {
                            await submittedAmount.add(row.data.getCurrency("settlement_total"));
                        }
                    }

                    const totalAmountMessage = `${submittedAmount.toString()}`;
                    const actionVerb = is_update ? "updated" : "submitted";
                    const destinationText = is_update ? "" : " to Triumph";

                    // Show success message
                    await this.slideOut();

                    const panelContainer = new Panel({ align: HorizontalAlignment.CENTER });
                    const headerMessage = new Label({
                        text: "Success!",
                        fontSize: "large",
                        rowBreak: true,
                        align: HorizontalAlignment.CENTER
                    });

                    const successMessage = new Label({
                        text: `${successful_settlement_count} settlement(s) ${actionVerb}${destinationText} for a total of ${totalAmountMessage}.`,
                        rowBreak: true,
                        align: HorizontalAlignment.CENTER,
                    });

                    panelContainer.add(headerMessage);
                    panelContainer.add(successMessage);
                    Toast.showToast(panelContainer, {backgroundColor: "success"}, {millisUntilDismissal: 10000});

                    // Show error notification if there were partial failures
                    if (failure_count > 0 && error) {
                        TriumphNotifications.showErrorSnackbar(error, is_update);
                    }
                }else {
                    // All submissions failed
                    TriumphNotifications.showErrorSnackbar(error, is_update);
                }
            } catch (error) {
                Snackbar.showWarningSnackbar("Failed to connect to Triumph. Please try again later.");
            } finally {
                this.readyToSubmitButton.caption = "Submit";
                this.readyToSubmitButton.busy = false;
            }
        }

    });

   static async calculateSettlementTotal(selectedRowData: ModelRow<any>[], settlementsTable: SubmittalSettlementsTable, isReadyToSubmit: boolean): Promise<Currency> {
        if (selectedRowData.length === 0) {
            settlementsTable.textboxTotalAmount.text = "";
            settlementsTable.textboxTotalAmount.color = "";
            settlementsTable.textboxTotalAmount.tooltip = null;
            return new Currency({ amount: 0 });
        }

        // Get currency from first settlement
        const baseCurrency = selectedRowData[0].getCurrency("settlement_total");
        const currencyCode = baseCurrency.currency_code;
        const total = new Currency({ currency_code: currencyCode, amount: 0 });
        let hasMixedCurrencies = false;

        for (const row of selectedRowData) {
            const settlementCurrency = row.getCurrency("settlement_total");

            // Check for currency mismatch
            if (settlementCurrency.currency_code !== currencyCode) {
                hasMixedCurrencies = true;
                continue;
            }

            await total.add(settlementCurrency);
        }

        const parentLayout = settlementsTable.getParentLayout() as ReadyToSubmitSlideout;
        const submitButton = parentLayout.readyToSubmitButton;

        if (isReadyToSubmit) {
            const hasSelection = settlementsTable.tableReadyToSubmitOrderPayables.selectedRows.length > 0;
            submitButton.enabled = hasSelection && !hasMixedCurrencies && total.amount > 0;

            if (!hasSelection) {
                submitButton.disabledTooltip = "Please select at least one settlement to submit";
            } else if (hasMixedCurrencies) {
                submitButton.disabledTooltip = "Cannot submit settlements with different currencies";
            } else if (total.amount <= 0) {
                submitButton.disabledTooltip = "Total amount must be greater than zero";
            } else {
                submitButton.disabledTooltip = null;
            }
        }

        settlementsTable.textboxTotalAmount.text = total.toString();

        if (hasMixedCurrencies) {
            settlementsTable.textboxTotalAmount.color = "error";
            settlementsTable.textboxTotalAmount.tooltip = "Selected settlements have different currencies";
        } else if (total.amount <= 0) {
            settlementsTable.textboxTotalAmount.color = "error";
            settlementsTable.textboxTotalAmount.tooltip = "Total amount must be greater than zero";
        } else {
            settlementsTable.textboxTotalAmount.color = isReadyToSubmit ? "success" : "";
            settlementsTable.textboxTotalAmount.tooltip = null;
        }

        return total;
    }

    static async populateExcludedSettlementRows(slideoutLayout: ReadyToSubmitSlideout, allRows: TableRow[]) {
        // populate the excluded settlement rows
        const excludedSubmittalSettlements =  slideoutLayout.layoutExcludedSubmittalSettlements;

        const excludedSettlementRows = allRows.filter(row => row.selected === false);
        const modelRows = excludedSettlementRows.map(row => row.data);

        // Set table visibility based on excluded rows
        const hasExcludedRows = excludedSettlementRows.length > 0;
        excludedSubmittalSettlements.visible = hasExcludedRows;

        if (hasExcludedRows) {
            excludedSubmittalSettlements.tableReadyToSubmitOrderPayables.selectionMode = SelectionMode.NONE;
            excludedSubmittalSettlements.tableReadyToSubmitOrderPayables.dataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
            excludedSubmittalSettlements.labelSubmitCount.text = `Excluded Settlements (${excludedSettlementRows.length})`;
            //total up the settlement_total and display on the total amount textbox
            await this.calculateSettlementTotal(modelRows, excludedSubmittalSettlements, false);
        }
    }
}
